import type { RuleGroupType } from 'react-querybuilder';

import type { SuggestionOption } from '~/components/core/Editor/types';
import type { TEMPLATES_TYPES } from '~/components/GenericTemplates/utils/genericTemplatesUtils';
import type {
  GenericTemplateSectionsResponseGenericTemplateSectionResponse,
  GenericTemplatesMetadataRequest,
} from '~/server_shared/openapi/data-contracts';
import type { CLAIM_NOTIFICATIONS_PRIORITY } from '~/Types';

// Sourced from the generated request contract rather than restated as `string`: the backend rejects a
// sort_by that is not one of its sortable columns, so marking a new table column sortable has to fail
// here at compile time instead of turning a header click into a 400.
export type TemplatesSortBy = NonNullable<GenericTemplatesMetadataRequest['sort_by']>;
export type TemplatesOrderBy = NonNullable<GenericTemplatesMetadataRequest['order_by']>;

export interface PaginationProps {
  page: number;
  limit: number;
  sort_by: TemplatesSortBy;
  order_by: TemplatesOrderBy;
}

export interface TemplateMetadata {
  id: number;
  template_name: string;
  template_type: TemplateTypeKey;
  last_update_datetime: Date;
  sub_organization_ids: number[];
  lobs: string[];
  countries?: string[];
  is_all_countries?: boolean;
  states?: string[];
  coverage_keys?: string[];
  is_claim_level?: boolean;
  is_enabled: boolean;
  dynamic_token_fields: string[];
  connected_automatic_rule_actions_display_names?: string[];
  document_type?: string;
  comment?: string;
  is_all_coverages?: boolean;
  document_template_id?: number | null;
}

export interface DocumentTemplateMetadata extends TemplateMetadata {
  is_attached_to_email_template?: boolean;
}

export type TemplateTypeKey = keyof typeof TEMPLATES_TYPES;

export interface TemplateBuilderEditorInnerComponentProps {
  disabled: boolean;
  suggestions?: SuggestionOption[];
}

export interface TemplateData {
  id: number;
  title_template?: string;
  body_template: string;
}

export interface TemplateFormSubmitValuesProps {
  is_all_suborgs: boolean;
  sub_organization_ids: number[];
  is_all_lobs: boolean;
  lobs: string[];
  coverage_keys: string[];
  is_all_coverages: boolean;
  countries?: string[];
  is_all_countries?: boolean;
  states: string[];
  is_all_states: boolean;
  is_enabled: boolean;
  template_name: string;
  template_type: string;
  is_claim_level: boolean;
  body_template: string;
  title_template: string;
  id?: number;
  dynamic_token_fields?: string[];
  sections?: TemplateSectionFormValue[];
}

/**
 * A conditional section of the body, as the API serves it: the key its `data-section-key` wrapper carries,
 * its label and its condition. The schema types the condition as a plain object; the form holds a query-builder group.
 */
export type TemplateSectionFormValue = Omit<
  GenericTemplateSectionsResponseGenericTemplateSectionResponse,
  'conditions_json'
> & {
  conditions_json: RuleGroupType | null;
  /** Only a changed condition is sent: an omitted one keeps the stored rule, and needs no permission. */
  isConditionChanged: boolean;
};

export interface AdvancedSearchFilterTypes {
  coverage_keys: string[];
  country: string;
  states: string[];
  sub_organization_ids: number[];
  lobs: string[];
  template_name: string;
}

export type GenericTemplatesRoutes = {
  GET_TEMPLATES: (organizationId: number) => string;
  CREATE_TEMPLATE: (organizationId: number) => string;
  GET_TEMPLATE: (organizationId: number, template_id: number) => string;
  GENERATE_FROM_TEMPLATE: (params: {
    organizationId: number;
    templateId: number;
    claimId: number;
    exposureId?: number;
    recipientContactId?: number | string;
    contactId?: number | string;
    documentId?: number | string;
    paymentRequestId?: number | string;
  }) => string;
  UPDATE_TEMPLATE: (organizationId: number, templateId: number) => string;
  UPDATE_TEMPLATE_IS_ENABLED: (organizationId: number, templateId: number) => string;
  GENERATE_VIDEO_INVITATION: (params: { organizationId: number; claimId: number; communicationId: number }) => string;
};

export interface NotificationTemplateFormSubmitValuesProps extends TemplateFormSubmitValuesProps {
  priority?: keyof typeof CLAIM_NOTIFICATIONS_PRIORITY | null;
  category_id?: number | null;
  is_due_date_immediate?: boolean | null;
  due_date_number?: number | null;
  due_date_timeframe?: string | null;
}

// --- Added: Backend-aligned type for GenericTemplateResponse ---
export interface GenericTemplateResponse {
  id: number;
  external_template_id?: string | null;
  is_all_suborgs?: boolean | null;
  sub_organization_ids?: number[] | null;
  is_all_lobs?: boolean | null;
  lobs?: string[] | null;
  is_all_coverages?: boolean | null;
  coverage_keys?: string[] | null;
  countries?: string[] | null;
  is_all_countries?: boolean | null;
  states?: string[] | null;
  is_all_states?: boolean | null;
  is_enabled: boolean;
  template_name: string;
  template_type: TemplateTypeKey;
  is_claim_level: boolean;
  body_template: string;
  title_template?: string | null;
  datetime_created: string; // ISO string
  datetime_enabled?: string | null;
  last_update_datetime: string; // ISO string
  connected_automatic_rule_actions_display_names?: string[] | null;
  dynamic_token_fields?: string[] | null;
  priority?: keyof typeof CLAIM_NOTIFICATIONS_PRIORITY | null;
  category_id?: number | null;
  is_due_date_immediate?: boolean | null;
  due_date_number?: number | null;
  due_date_timeframe?: string | null;
  document_template_id?: number | null;
}

// Utility: Convert GenericTemplateResponse to TemplateMetadata
export function genericTemplateResponseToTemplateMetadata(data: GenericTemplateResponse): TemplateMetadata {
  return {
    id: data.id,
    template_name: data.template_name,
    template_type: data.template_type as TemplateTypeKey,
    last_update_datetime: new Date(data.last_update_datetime),
    sub_organization_ids: data.sub_organization_ids ?? [],
    lobs: data.lobs ?? [],
    countries: data.countries ?? undefined,
    is_all_countries: data.is_all_countries ?? undefined,
    states: data.states ?? undefined,
    coverage_keys: data.coverage_keys ?? undefined,
    is_claim_level: data.is_claim_level ?? undefined,
    is_enabled: data.is_enabled,
    dynamic_token_fields: data.dynamic_token_fields ?? [],
    connected_automatic_rule_actions_display_names: data.connected_automatic_rule_actions_display_names ?? undefined,
    document_type: undefined, // Not present in response
    comment: undefined, // Not present in response
    is_all_coverages: data.is_all_coverages ?? undefined,
  };
}

// Utility: Convert GenericTemplateResponse to TemplateFormSubmitValuesProps
export function genericTemplateResponseToFormValues(
  data: GenericTemplateResponse
): TemplateFormSubmitValuesProps | NotificationTemplateFormSubmitValuesProps | EmailTemplateFormSubmitValuesProps {
  const formValues: TemplateFormSubmitValuesProps = {
    is_all_suborgs: data.is_all_suborgs ?? false,
    sub_organization_ids: data.sub_organization_ids ?? [],
    is_all_lobs: data.is_all_lobs ?? false,
    lobs: data.lobs ?? [],
    coverage_keys: data.coverage_keys ?? [],
    is_all_coverages: data.is_all_coverages ?? false,
    countries: data.countries ?? undefined,
    is_all_countries: data.is_all_countries ?? undefined,
    states: data.states ?? [],
    is_all_states: data.is_all_states ?? false,
    is_enabled: data.is_enabled,
    template_name: data.template_name,
    template_type: data.template_type,
    is_claim_level: data.is_claim_level,
    body_template: data.body_template,
    title_template: data.title_template ?? '',
    id: data.id,
    dynamic_token_fields: data.dynamic_token_fields ?? [],
  };
  if (data.template_type === 'notification') {
    return {
      ...formValues,
      priority: data.priority ?? null,
      category_id: data.category_id ?? null,
      is_due_date_immediate: data.is_due_date_immediate ?? null,
      due_date_number: data.due_date_number ?? null,
      due_date_timeframe: data.due_date_timeframe ?? null,
    };
  }
  if (data.template_type === 'email') {
    return {
      ...formValues,
      document_template_id: data.document_template_id ?? null,
    };
  }
  return formValues;
}

export interface EmailTemplateFormSubmitValuesProps extends TemplateFormSubmitValuesProps {
  document_template_id?: number | null;
}

export interface DocumentTemplatesSearchParams {
  is_all_sub_orgs?: boolean;
  sub_organization_ids?: number[];
  is_all_lobs?: boolean;
  lobs?: string[];
  coverage_keys?: string[];
  is_all_coverages?: boolean;
  country?: string;
  states?: string[];
  is_claim_level?: boolean;
  is_strict?: boolean;
}

export interface TemplatesApiResponse {
  templates_metadata: TemplateMetadata[];
  count: number;
}
